<template>
  <div class="content-wrapper">
    <div class="vld-parent">
      <loading :active.sync="isLoading" :is-full-page="fullPage"></loading>
    </div>

    <breadcrumb :page="$t('form.student.studentList')"></breadcrumb>

    <div class="content">
      <div class="container-fluid">
        <b-alert v-model="alert" :variant="alertClass" dismissible fade>{{
          alertMessage
        }}</b-alert>

        <!-- Filters -->
        <div class="card">
          <div class="card-header bg-light">
            <h3 class="card-title">{{ $t("filters") }}</h3>
            <div class="text-right">
              <b-button
                variant="primary"
                size="sm"
                @click="detailedSearch = !detailedSearch"
              >
                {{ $t("button.detailedSearch") }}</b-button
              >
              <b-button variant="default" size="sm" @click="clearSearch()"
                >{{ $t("button.resetFilter") }}
              </b-button>
            </div>
          </div>

          <div class="card-body">
            <form role="form">
              <div class="row">
                <div class="col-sm-6">
                  <div class="form-group">
                    <label>{{ $t("form.student.searchCriteria") }}</label>
                    <v-select
                      @input="loadPreset"
                      label="name"
                      :options="presetList"
                      v-model="preset"
                    ></v-select>
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-sm-3">
                  <div class="form-group">
                    <label>{{ $t("form.student.studentName") }}</label>
                    <input
                      type="text"
                      class="form-control"
                      @keyup="searchStudents"
                      v-model="search.name"
                    />
                  </div>
                </div>
                <div class="col-sm-2">
                  <div class="form-group">
                    <label>{{ $t("form.student.studentNumber") }}</label>
                    <input
                      type="number"
                      class="form-control"
                      @keyup="searchStudents"
                      v-model="search.number"
                    />
                  </div>
                </div>
                <div class="col-sm-2">
                  <div class="form-group">
                    <label>{{ $t("form.student.matriculationNum") }}</label>
                    <input
                      type="text"
                      class="form-control"
                      @keyup="searchStudents"
                      v-model="search.matriculationnumber"
                    />
                  </div>
                </div>
                <div class="col-sm-3">
                  <div class="form-group">
                    <div class="d-flex justify-content-between">
                      <label>{{ $t("form.student.programStart") }}</label>
                      <span>
                        <label>
                          <input
                            type="checkbox"
                            @change="checkProgramStart('month')"
                            v-model="programStartMonth"
                          />
                          {{ $t("form.student.byMonth") }}
                        </label>

                        <label>
                          <input
                            type="checkbox"
                            @change="checkProgramStart('year')"
                            v-model="programStartYear"
                          />
                          {{ $t("form.student.byYear") }}
                        </label>
                      </span>
                    </div>

                    <date-picker
                      :lang="lang"
                      v-if="programStartMonth"
                      @change="searchStudents"
                      class="date-picker"
                      v-model="search.programStartMonth"
                      valueType="format"
                      format="MM.YYYY"
                      type="month"
                      :popup-style="{ top: '100%', left: 0 }"
                      :append-to-body="false"
                    >
                    </date-picker>

                    <date-picker
                      :lang="lang"
                      v-else-if="programStartYear"
                      class="date-picker"
                      v-model="search.programStartYear"
                      @change="searchStudents"
                      valueType="format"
                      format="YYYY"
                      type="year"
                      :popup-style="{ top: '100%', left: 0 }"
                      :append-to-body="false"
                    >
                    </date-picker>

                    <date-picker
                      :lang="lang"
                      v-else
                      class="date-picker"
                      v-model="search.programStart"
                      @change="searchStudents"
                      valueType="format"
                      :format="dateFormat"
                      :popup-style="{ top: '100%', left: 0 }"
                      :append-to-body="false"
                    >
                    </date-picker>
                  </div>
                </div>
                <div class="col-sm-2">
                  <div class="form-group">
                    <label>{{ $t("form.student.status") }}</label>
                    <v-select
                      @input="searchStudents"
                      label="StatusName"
                      :options="statusList"
                      v-model="search.Status"
                      :reduce="(statusList) => statusList.StatusID"
                    >
                    </v-select>
                  </div>
                </div>

                <div class="col-sm-3">
                  <div class="form-group">
                    <label>{{ $t("form.student.programType") }}</label>
                    <v-select
                      @input="searchStudents"
                      label="FullTitle"
                      :options="programTypes"
                      v-model="search.programType"
                      :reduce="(programsTypes) => programsTypes"
                    ></v-select>
                  </div>
                </div>

                <div class="col-sm-3">
                  <div class="form-group">
                    <label
                      >{{ $t("form.student.program") }}
                      {{ $t("form.student.academicYear") }}</label
                    >
                    <v-select
                      @input="searchStudents"
                      label="FullTitle"
                      :options="programAcademicYears"
                      v-model="search.programAcademicYear"
                      :reduce="(programAcademicYears) => programAcademicYears"
                    ></v-select>
                  </div>
                </div>

                <div class="col-sm-3">
                  <div class="form-group">
                    <label>{{ $t("form.student.country") }}</label>
                    <v-select
                      @input="searchStudents"
                      label="printable_name"
                      :options="countries"
                      v-model="search.country"
                      :reduce="(countries) => countries.printable_name"
                    >
                    </v-select>
                  </div>
                </div>

                <div class="col-sm-3">
                  <div class="form-group">
                    <label>{{ $t("form.student.partnerCode") }}</label>
                    <v-select
                      label="PartnerCode"
                      :options="partners"
                      v-model="search.partnerId"
                      @input="
                        partnerFilter();
                        fetchPartnerCampus();
                      "
                      :reduce="(partners) => partners.PartnerID"
                    >
                    </v-select>
                  </div>
                </div>

                <template v-if="detailedSearch">
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.partnerCampus") }}</label>
                      <v-select
                        @input="searchStudents"
                        label="PartnerCampusLocation"
                        :options="partnercampus"
                        v-model="search.partnerCampus"
                        :reduce="
                          (partnercampus) => partnercampus.PartnerCampusID
                        "
                        :resetOnOptionsChange="true"
                      ></v-select>
                    </div>
                  </div>

                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.program") }}</label>
                      <v-select
                        @input="searchStudents"
                        label="FullTitle"
                        :options="programs"
                        v-model="search.programId"
                        :reduce="(programs) => programs.ID"
                      ></v-select>
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.title") }}</label>
                      <v-select
                        @input="searchStudents"
                        :options="titles"
                        v-model="search.title"
                        :reduce="(titles) => titles"
                      ></v-select>
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.numberFrom") }}</label>
                      <input
                        type="text"
                        v-model="search.numberRange.from"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.numberTo") }}</label>
                      <input
                        type="text"
                        v-model="search.numberRange.to"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.firstName") }}</label>
                      <input
                        type="text"
                        v-model="search.firstName"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.lastName") }}</label>
                      <input
                        type="text"
                        v-model="search.lastName"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.transcriptName") }}</label>
                      <input
                        type="text"
                        v-model="search.transcriptName"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <div class="d-flex justify-content-between">
                        <label>{{ $t("form.student.birthDate") }}</label>
                        <span>
                          <label>
                            <input
                              type="checkbox"
                              @change="checkBirthDate('month')"
                              v-model="birthDateMonth"
                            />
                            {{ $t("form.student.byMonth") }}
                          </label>
                          <label>
                            <input
                              type="checkbox"
                              @change="checkBirthDate('year')"
                              v-model="birthDateYear"
                            />
                            {{ $t("form.student.byYear") }}
                          </label>
                        </span>
                      </div>

                      <date-picker
                        :lang="lang"
                        v-if="birthDateMonth"
                        class="date-picker"
                        v-model="search.birthDateMonth"
                        @change="searchStudents"
                        valueType="format"
                        format="MM.YYYY"
                        type="month"
                        :popup-style="{ top: '100%', left: 0 }"
                        :append-to-body="false"
                      >
                      </date-picker>

                      <date-picker
                        :lang="lang"
                        v-else-if="birthDateYear"
                        class="date-picker"
                        v-model="search.birthDateYear"
                        @change="searchStudents"
                        valueType="format"
                        format="YYYY"
                        type="year"
                        :popup-style="{ top: '100%', left: 0 }"
                        :append-to-body="false"
                      >
                      </date-picker>

                      <date-picker
                        :lang="lang"
                        v-else
                        @change="searchStudents"
                        class="date-picker"
                        v-model="search.birthDate"
                        valueType="format"
                        :format="dateFormat"
                        :popup-style="{ top: '100%', left: 0 }"
                        :append-to-body="false"
                      >
                      </date-picker>
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.birthPlace") }}</label>
                      <input
                        type="text"
                        v-model="search.birthPlace"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.birthCountry") }}</label>
                      <v-select
                        @input="searchStudents"
                        label="printable_name"
                        :options="countries"
                        v-model="search.birthCountry"
                        :reduce="(countries) => countries.printable_name"
                      ></v-select>
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.nationality") }}</label>
                      <input
                        type="text"
                        v-model="search.nationality"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.streetAddress") }}</label>
                      <input
                        type="text"
                        v-model="search.streetAddress"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.city") }}</label>
                      <input
                        type="text"
                        v-model="search.city"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.postalCode") }}</label>
                      <input
                        type="text"
                        v-model="search.zip"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.phone") }}</label>
                      <input
                        type="text"
                        v-model="search.phone"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.mobile") }}</label>
                      <input
                        type="text"
                        v-model="search.mobile"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{ $t("form.student.email") }}</label>
                      <input
                        type="text"
                        v-model="search.email"
                        @keyup="searchStudents"
                        class="form-control"
                      />
                    </div>
                  </div>
                  <div class="col-sm-3">
                    <div class="form-group">
                      <label>{{
                        $t("form.student.legalizationOption1")
                      }}</label>
                      <select
                        class="form-control"
                        v-model="search.legalization"
                        @change="searchStudents"
                      >
                        <option value selected>
                          {{ $t("form.student.select") }}
                        </option>
                        <option value="1">{{ $t("form.student.yes") }}</option>
                        <option value="0">{{ $t("form.student.no") }}</option>
                      </select>
                    </div>
                  </div>
                  <div class="col-sm-6">
                    <div class="form-group">
                      <label>{{ $t("form.student.notes") }}</label>
                      <textarea
                        v-model="search.notes"
                        @keyup="searchStudents"
                        rows="5"
                        class="form-control"
                      ></textarea>
                    </div>
                  </div>
                  <div class="col-12">
                    <hr />
                  </div>
                  <div class="col-sm-6">
                    <div class="form-group">
                      <input
                        type="text"
                        v-model="preset.name"
                        class="form-control"
                        :placeholder="$t('form.student.presetName')"
                      />
                    </div>
                  </div>
                  <div class="col-md-6">
                    <button
                      class="btn btn-primary"
                      type="button"
                      @click="savePreset"
                    >
                      {{ $t("button.save") }}
                    </button>
                    <button
                      class="btn btn-danger"
                      type="button"
                      v-show="presetSelected"
                      @click="deletePreset"
                    >
                      {{ $t("button.delete") }}
                    </button>
                  </div>
                </template>
              </div>
            </form>
          </div>
        </div>

        <!-- Student's List -->
        <div class="card">
          <div class="card-header bg-light">
            <div class="d-flex justify-content-between">
              <div class="card-title">
                {{ $t("form.student.studentList") }} ({{ pagination.total }})
              </div>
              <div class="d-flex">
                <div
                  class="text-nowrap mr-1"
                  v-if="downloadToggle && students.length"
                >
                  <button
                    class="btn btn-danger btn-sm"
                    @click="generate('pdf')"
                  >
                    <i class="fa fa-download" aria-hidden="true"></i>
                    {{ $t("button.pdf") }}
                  </button>
                  <button
                    class="btn btn-success btn-sm"
                    @click="generate('excel')"
                  >
                    <i class="fa fa-download" aria-hidden="true"></i>
                    {{ $t("button.excel") }}
                  </button>
                  <button
                    class="btn btn-primary btn-sm"
                    @click="generate('word')"
                  >
                    <i class="fa fa-download" aria-hidden="true"></i>
                    {{ $t("button.word") }}
                  </button>
                </div>

                <b-form-select
                  style="font-size: 100%"
                  @change="
                    getStudents();
                    $emit('per-page-updated', perPage);
                  "
                  v-model="perPage"
                  :options="[25, 50, 100, 500]"
                  size="sm"
                >
                </b-form-select>
              </div>
            </div>
          </div>

          <div class="card-body table-responsive p-0">
            <table class="table table-hover" v-if="students.length">
              <thead>
                <tr>
                  <th>{{ $t("form.student.studentNumber") }}</th>

                  <th>{{ $t("form.student.matriculationNum") }}</th>

                  <th>{{ $t("form.student.studentName") }}</th>

                  <th>{{ $t("form.student.programStart") }}</th>

                  <th>{{ $t("form.student.status") }}</th>

                  <th>{{ $t("form.student.program") }}</th>

                  <th>{{ $t("form.student.country") }}</th>

                  <th>{{ $t("form.student.partnerCode") }}</th>

                  <th>{{ $t("form.student.examScore") }}</th>

                  <th>{{ $t("form.student.applicationDocument") }}</th>

                  <th class="text-right fix-action">
                    {{ $t("form.student.action") }}
                  </th>
                </tr>
              </thead>

              <tbody v-for="(student, index) in students" v-bind:key="index">
                <tr @dblclick="editStudent(index)">
                  <td>{{ student.StudentNumber }}</td>
                  <td>{{ student.MatriculationNum }}</td>
                  <td>{{ student.FirstName }} {{ student.LastName }}</td>
                  <td>{{ student.ProgramStart | formatDate(dateFormat) }}</td>
                  <td>
                    {{
                      student.current_status
                        ? student.current_status.StatusName
                        : ""
                    }}
                  </td>
                  <td>
                    <span v-if="student.programs.length">{{
                      student.programs[0] | program
                    }}</span>
                  </td>
                  <td>{{ student.Country }}</td>
                  <td>
                    {{ student.partner ? student.partner.PartnerCode : "" }}
                  </td>
                  <td>
                    <b-button size="sm" @click="showExamScores(student)">
                      <span class="fa fa-eye"></span>
                    </b-button>
                  </td>
                  <td>
                    <span
                      class="fa fa-check text-success"
                      v-if="isValidApplicationDocuments(student)"
                    ></span>
                  </td>
                  <td class="text-right">
                    <button
                      class="btn btn-info btn-sm"
                      v-on:click="copyStudent(student)"
                      v-show="permissions.createStudents"
                    >
                      <span class="fa fa-copy"></span>
                    </button>
                    <button
                      class="btn btn-info btn-sm"
                      v-on:click="editStudent(index)"
                      v-show="permissions.viewStudents"
                    >
                      <span class="fa fa-edit"></span>
                    </button>
                    <button
                      class="btn btn-danger btn btn-sm"
                      v-on:click="deletestudent(index)"
                      v-show="permissions.deleteStudents"
                    >
                      <span class="fa fa-trash"></span>
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
            <p class="p-3 m-0" v-else>{{ $t("form.student.noRecord") }}</p>
          </div>

          <pagination
            v-if="pagination.last_page > 1"
            :pagination="pagination"
            :offset="5"
            @paginate="paginate()"
          ></pagination>
        </div>

        <b-modal id="examScoreModel" title="Exam Scores">
          <div class="table-responsive p-0">
            <table class="table table-bordered">
              <thead>
                <tr class="header">
                  <th>{{ $t("form.student.examName") }}</th>
                  <th>{{ $t("form.student.examScore") }}</th>
                </tr>
              </thead>

              <tbody>
                <tr v-if="examScores.BBAExitSurvey">
                  <td>{{ $t("form.student.bachelorProgramOption1") }}</td>
                  <td>{{ examScores.BBAExitSurveyResult }}</td>
                </tr>
                <tr v-if="examScores.MASExitExamSBS">
                  <td>{{ $t("form.student.masterProgramOption2") }}</td>
                  <td>{{ examScores.MASExitExamSBSResult }}</td>
                </tr>
                <tr v-if="examScores.MASExitExamExternal">
                  <td>{{ $t("form.student.masterProgramOption3") }}</td>
                  <td>{{ examScores.MASExitExamExternalResult }}</td>
                </tr>
                <tr v-if="examScores.DBAExitExamSBS">
                  <td>{{ $t("form.student.doctorateProgramOption2") }}</td>
                  <td>{{ examScores.DBAExitExamSBSResult }}</td>
                </tr>
                <tr v-if="examScores.DBAExitExamExternal">
                  <td>{{ $t("form.student.doctorateProgramOption3") }}</td>
                  <td>{{ examScores.DBAExitExamExternalResult }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </b-modal>
      </div>
    </div>
  </div>
</template>
<script>
export default {
  props: ["permissions", "dateFormat", "user"],

  data() {
    return {
      programStartMonth: false,
      programStartYear: false,

      birthDateMonth: false,
      birthDateYear: false,

      fullPage: true,
      isLoading: false,

      detailedSearch: false,
      downloadToggle: false,

      alert: false,
      alertClass: "",
      alertMessage: "",

      presetSelected: false,
      request_source: "",
      perPage: this.user.per_page,

      preset: {
        name: "",
        entity: "student",
        value: {},
      },

      search: {
        title: "",
        status: "",
        country: "",
        programId: "",
        partnerId: "",
        birthCountry: "",
        partnerCampus: "",
        graduate: "",
        legalization: "",
        numberRange: {},
        programAcademicYear: "",
        programType: "",
      },
      lang: {
        formatLocale: {
          firstDayOfWeek: 1,
        },
      },
      pagination: {
        current_page: 1,
      },

      students: [],
      partnercampus: [],
      statusList: [],
      programs: [],
      programTypes: [],
      programAcademicYears: [],
      countries: [],
      partners: [],
      titles: [],
      presetList: [],
      examScores: {},
    };
  },

  async mounted() {
    this.isLoading = true;

    if (!this.permissions.viewStudents) {
      this.$router.push("/");
      return;
    }

    await this.getFillterList();

    if (localStorage.getItem("filter-student")) {
      this.search = JSON.parse(localStorage.getItem("filter-student"));

      if (this.search.partnerId) {
        let campusId = this.search.partnerCampus;
        await this.fetchPartnerCampus();
        this.search.partnerCampus = campusId;
      }
    }

    this.getStudents();
    this.getPresets();
  },

  watch: {
    birthDate: function (newValue, oldValue){
        
          delete this.search.birthDateMonth;
          delete this.search.birthDateYear;
    },
    birthDateYear: function(newValue, oldValue) {
          delete this.search.birthDate;
          delete this.search.birthDateMonth;
          delete this.search.birthDateYear;
    },
    birthDateMonth: function(newValue, oldValue) {
        delete this.search.birthDate;
        delete this.search.birthDateMonth;delete this.search.birthDateMonth;
        delete this.search.birthDateYear;
    },
    search: {
      handler(newSearch) {
        localStorage.setItem("filter-student", JSON.stringify(this.search));

        const emojiRegex = /[\u{10000}-\u{10FFFF}]/gu;
        
        // Loop through all properties in the search object
        Object.keys(newSearch).forEach(key => {
          // Only process string values
          if (typeof newSearch[key] === 'string' && emojiRegex.test(newSearch[key])) {
            // Strip the emoji and update the reactive property
            this.search[key] = newSearch[key].replace(emojiRegex, '');
          }
        });
      },
      deep: true
    }
  },

  methods: {
    /**
    * Copy student record
    */
    async copyStudent(student) {
      if (!confirm(this.$t('form.student.confirmCopy'))) {
        return;
      }

      this.isLoading = true;
      
      try {
        const response = await axios.post(`/api/students/${student.StudentNumber}/copy`);
        
        if (response.data.success) {
          this.showAlert(response.data.message);
          // Refresh the student list to show the new copied record
          this.getStudents();
          
          // Navigate to edit the new student
          this.$router.push({
            name: "StudentEdit",
            params: {
              id: response.data.newStudentNumber
            }
          });
        }
      } catch (error) {
        this.showAlert(error.response.data.message || 'Error copying student', 'danger');
      } finally {
        this.isLoading = false;
      }
    },
    checkProgramStart(val) {
      if (val == "month") {
        this.programStartYear = false;
        if (
          this.programStartMonth &&
          (this.search.programStart || this.search.programStartYear)
        ) {
          delete this.search.programStart;
          delete this.search.programStartYear;
          this.searchStudents();
        } else if (this.search.programStartMonth) {
          delete this.search.programStart;
          delete this.search.programStartMonth;
          delete this.search.programStartYear;
          this.searchStudents();
        }
      }
      if (val == "year") {
        this.programStartMonth = false;

        if (
          this.programStartYear &&
          (this.search.programStart || this.search.programStartMonth)
        ) {
          delete this.search.programStart;
          delete this.search.programStartMonth;
          this.searchStudents();
        } else if (this.search.programStartYear) {
          delete this.search.programStart;
          delete this.search.programStartMonth;
          delete this.search.programStartYear;
          this.searchStudents();
        }
      }
    },

    checkBirthDate(val) {
      if (val == "month") {
        this.birthDateYear = false;
        if (
          this.birthDateMonth &&
          (this.search.birthDate || this.search.birthDateYear)
        ) {
          delete this.search.birthDate;
          delete this.search.birthDateYear;
          this.searchStudents();
        } else if (this.search.birthDateMonth) {
          delete this.search.birthDate;
          delete this.search.birthDateMonth;
          delete this.search.birthDateYear;
          this.searchStudents();
        }
      }
      if (val == "year") {
        this.birthDateMonth = false;
        if (
          this.birthDateYear &&
          (this.search.birthDate || this.search.birthDateMonth)
        ) {
          delete this.search.birthDate;
          delete this.search.birthDateMonth;
          this.searchStudents();
        } else if (this.search.birthDateYear) {
          delete this.search.birthDate;
          delete this.search.birthDateMonth;
          delete this.search.birthDateYear;
          this.searchStudents();
        }
      }
    },

    isValidApplicationDocuments(student) {
      let application = student.application;
      let program = student.programs[0] ? student.programs[0] : null;

      if (application && program) {
        // BBA
        if (program.ProgramCode == "BBA") {
          return (
            application.ValidCurriculumVitae &&
            application.ValidDegree &&
            application.ValidTranscript &&
            application.ValidLetter &&
            application.ValidPicture &&
            application.ValidEnglishProficiency
          );
        }

        // MSc & MBA & DBA
        return (
          application.ValidCurriculumVitae &&
          application.ValidDegree &&
          application.ValidTranscript &&
          application.ValidLetter &&
          application.ValidPicture
        );
      }

      return false;
    },

    /**
     * Build Query String for Filters
     */
    buildQueryString(obj) {
      let query = "";
      Object.keys(obj).forEach(function (key) {
        if (key == "numberRange" && obj[key].from && obj[key].to) {
          query += key + "=" + encodeURIComponent(obj[key].from) + "," + encodeURIComponent(obj[key].to) + "&";
        } 
        else if (key == "numberRange" && obj[key].from && !obj[key].to) {
          query += "from" + "=" + encodeURIComponent(obj[key].from) + "&";
        } 
        else if (key == "numberRange" && !obj[key].from && obj[key].to) {
          query += "to" + "=" + encodeURIComponent(obj[key].to) + "&";
        } 
        else if (obj[key] != "" && key != "numberRange" && obj[key] != null) {
          query += key + "=" + encodeURIComponent(obj[key]) + "&";
        }
      });

      return query.slice(0, -1);
    },

    async createToken(type) {
      let response = await axios.post("/api/api-tokens", {
        type,
      });
      let token = "";

      if (response.status == 200) {
        token = response.data.extra[0].token;
      }

      return token;
    },

    /**
     * Export students list - PDF | Excel | Word
     */
    async generate(method) {
      let token = await this.createToken("students-export");
      if (token == "") return;

      let filters = await this.buildQueryString(this.search);
      window.open(
        `/students/export?method=${method}&${filters}&recordType=student&token=${token}`,
        "_blank"
      );
    },

    /**
     * Save Preset into database
     */
    savePreset() {
      this.isLoading = true;
      this.alert = false;

      this.preset.value = JSON.stringify(this.search);

      axios.post("/api/presets", this.preset).then(
        (response) => {
          this.isLoading = false;
          this.showAlert(response.data.message);
          this.getPresets();
        },
        (error) => {
          this.isLoading = false;

          if (error.response.status == 422)
            this.showAlert(error.response.data.errors.name[0], "danger");
        }
      );
    },

    /**
     * Get Preset List
     */
    getPresets() {
      axios.get("/api/presets/?entity=" + this.preset.entity).then(
        (response) => {
          this.presetList = response.data;
        },
        (error) => {
          console.log(error.response);
        }
      );
    },

    /**
     * Load Preset and apply Filters
     */
    loadPreset() {
      if (!this.preset) {
        this.preset = {
          entity: "student",
        };
        this.clearSearch();
        this.alert = false;
        this.presetSelected = false;
        return;
      }
      this.alert = false;
      this.presetSelected = true;
      this.search = JSON.parse(this.preset.value);
      this.searchStudents();
    },

    /**
     * Delete the specific preset
     */
    deletePreset() {
      if (!confirm(this.$t("form.validation.delete"))) {
        return;
      }

      axios
        .delete("/api/presets/" + this.preset.id)
        .then((response) => {
          if (response.data.status == "success") {
            this.showAlert(response.data.message);
            this.getPresets();
            this.clearSearch();
            this.preset.name = "";
            this.preset.value = {};
            this.presetSelected = false;
          }
        })
        .catch((error) => {
          this.isLoading = false;
        });
    },

    /**
     * Reset applied filters
     */
    clearSearch() {
      this.search = {
        title: "",
        status: "",
        country: "",
        programId: "",
        partnerId: "",
        birthCountry: "",
        partnerCampus: "",
        graduate: "",
        legalization: "",
        numberRange: {},
        matriculationnumber: "",
      };

      this.searchStudents();
    },

    /**
     * Fetch partner campuses for selected partner
     */
    fetchPartnerCampus() {
      let partner = this.partners.find(
        (p) => p.PartnerID == this.search.partnerId
      );

      if (partner) {
        this.partnercampus = partner.campuses;
      }
    },

    /**
     * Get student list
     */
    getStudents() {
      let source = axios.CancelToken.source();

      if (this.request_source != "") {
        this.request_source.cancel("Operation canceled by the user.");
        console.clear();
      }

      this.request_source = source;

      this.isLoading = true;

      let filters = this.buildQueryString(this.search);

      if (filters != "") filters = "&" + filters;
      if (this.perPage) filters += `&limit=${this.perPage}`;

      axios
        .get(`/api/students?page=${this.pagination.current_page}${filters}`, {
          cancelToken: this.request_source.token,
        })
        .then((response) => {
          this.students = response.data.data.data;
          this.pagination = response.data.pagination;

          filters = filters.replace(`&limit=${this.perPage}`, "");
          this.downloadToggle = this.students.length && filters.length;
          this.isLoading = false;
        })
        .catch((error) => console.log(error));
    },

    /**
     * Apply paginate
     */
    paginate() {
      this.getStudents();
    },

    /**
     * Apply filters
     */
    searchStudents() {
      this.pagination.current_page = 1;
      this.getStudents();
    },

    /**
     *Apply partner filter and reset partner campus filter
     */
    partnerFilter() {
      this.search.partnerCampus = null;
      this.partnercampus = [];

      this.searchStudents();
    },

    /**
     * Get Filter List
     */
    async getFillterList() {
      let response = await axios.get("/api/students/initial-data");

      if (response) {
        let data = response.data;

        this.statusList = data.status.filter(status => status.StatusID !== 3);

        this.partners = data.partners;
        this.countries = data.countries;
        this.titles = data.titles;
        this.programAcademicYears = data.program_years;
        this.programTypes = data.program_types
          .filter((v, i, a) => a.indexOf(v) === i)
          .sort();

        this.programs = data.programs;
        this.programs.map(
          (p) => (p.FullTitle = this.$options.filters.program(p))
        );
      }
    },

    /**
     * Go to student edit page
     */
    editStudent(index) {
      if (!this.permissions.viewStudents) return;

      let id = this.students[index].StudentNumber;

      this.$router.push({
        name: "StudentEdit",

        params: {
          id: id,
        },
      });
    },

    /**
     * Show alert
     */
    showAlert(msg, cls = "success") {
      this.alertMessage = msg;
      this.alertClass = cls;
      this.alert = true;
    },

    /**
     * Delete student
     */
    deletestudent(index) {
      let conf = confirm(this.$t("form.validation.delete"));

      if (conf === true) {
        this.isLoading = true;
        axios
          .delete("/api/students/" + this.students[index].ID)
          .then((response) => {
            this.isLoading = false;
            this.students.splice(index, 1);
            this.showAlert(response.data.message);
            this.getStudents();
          })
          .catch((error) => {
            this.isLoading = false;
          });
      }
    },

    // Get Exam Score
    showExamScores(Student) {
      this.examScores = Student.graduatereq ? Student.graduatereq : {};
      this.$bvModal.show("examScoreModel");
    },
  },
};
</script>
